/**
 * useCarAutoSync — tiene allineata l'esperienza Android Auto (Ott 2026).
 *
 * COSA FA: quando l'utente è autenticato su app nativa Android, invia al
 * plugin nativo `CarBridge` lo snapshot dei percorsi salvati, così il display
 * del veicolo mostra dati aggiornati; al logout cancella lo snapshot (nessun
 * dato dell'utente precedente visibile in auto).
 *
 * ACCESSO PREMIUM: l'esperienza in auto è riservata a chi ha un abbonamento
 * attivo oppure la prova Premium in corso. Con `premium = false` lo snapshot
 * non contiene percorsi e l'auto mostra il messaggio di upgrade; appena
 * l'utente attiva l'abbonamento (o la prova) dal telefono il flag cambia e lo
 * snapshot viene riscritto SUBITO (nessuna attesa dei 5 minuti), quindi la
 * lista si sblocca senza ricollegare il veicolo.
 *
 * CADENZA: una sincronizzazione al cambio utente (o cambio stato premium), un
 * refresh ogni 5 minuti mentre l'app è aperta e — importante per l'auto — una
 * sincronizzazione **ogni volta che l'app torna in primo piano**
 * (`visibilitychange`): così basta aprire RideConnect sul telefono perché il
 * display del veicolo veda dati freschi, senza aspettare il timer.
 * Aggiornamenti immediati dopo un salvataggio percorso si ottengono con
 * `syncCarSnapshot()` (esportato da services/carBridge).
 *
 * Perché non a ogni render: lo snapshot è un JSON condiviso tra processi
 * (SharedPreferences) e va scritto solo quando serve — scritture continue
 * sarebbero rumore inutile sul file system e sul car host.
 */

import { useEffect, useRef } from 'react';
import useAuthStore from '@/store/useAuthStore';
import { clearCarSnapshot, isCarBridgeAvailable, syncCarSnapshot } from '@/services/carBridge';

export const CAR_SYNC_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Sincronizza subito lo snapshot fuori dal ciclo del hook.
 *
 * Serve dopo un cambiamento che l'auto deve vedere all'istante — per esempio un
 * percorso appena salvato (anche se la registrazione è partita dal display di
 * auto/moto): senza questa chiamata la lista in auto si aggiornerebbe solo al
 * prossimo refresh (5 minuti o rientro in primo piano dell'app).
 */
export async function syncCarSnapshotNow(): Promise<void> {
  if (!isCarBridgeAvailable()) return;
  const state = useAuthStore.getState();
  const user = state.user;
  await syncCarSnapshot({
    userName: user?.nickname ?? user?.username ?? null,
    premium: state.premium?.isPremium === true,
  });
}

/**
 * Richiesta di sincronizzazione "coalescente" (Milestone 1, 13 Ott 2026).
 *
 * Durante la registrazione lo snapshot cambia a ogni fix GPS (fino a 1 Hz):
 * chiamare `syncCarSnapshotNow` a ogni fix crea scritture sovrapposte sullo
 * stesso JSON (spreco e rischio di scrivere uno stato più vecchio del
 * precedente). Qui: mai due scritture in volo; se ne arriva una mentre una è
 * in corso, si ricorda e si esegue APPENA finisce — l'ultimo stato non si
 * perde mai.
 */
let carSyncInFlight = false;
let carSyncAgain = false;

export function requestCarSnapshotSync(): void {
  if (!isCarBridgeAvailable()) return;
  if (carSyncInFlight) {
    carSyncAgain = true;
    return;
  }
  carSyncInFlight = true;
  void syncCarSnapshotNow().finally(() => {
    carSyncInFlight = false;
    if (carSyncAgain) {
      carSyncAgain = false;
      requestCarSnapshotSync();
    }
  });
}

/**
 * @param userId   id dell'utente autenticato (null/undefined = logout)
 * @param userName nome mostrato dall'auto nel saluto del template
 *
 * Lo stato Premium arriva dai claim del JWT (`useAuthStore.premium`), la
 * stessa fonte usata da tutta l'app: `isPremium = true` copre abbonamento
 * attivo, prova in corso e piani dealer a pagamento; un `premium_until` nel
 * passato vale come non premium.
 */
export function useCarAutoSync(
  userId: string | null | undefined,
  userName?: string | null,
): void {
  const premium = useAuthStore((s) => s.premium?.isPremium === true);
  const lastSyncRef = useRef<number>(0);
  const lastUserRef = useRef<string | null>(null);
  const lastPremiumRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isCarBridgeAvailable()) return;

    if (!userId) {
      // Logout: pulizia immediata (l'auto non deve mostrare percorsi altrui)
      if (lastUserRef.current !== null) {
        lastUserRef.current = null;
        lastPremiumRef.current = null;
        lastSyncRef.current = 0;
        void clearCarSnapshot();
      }
      return;
    }

    const userChanged = lastUserRef.current !== userId;
    const premiumChanged = lastPremiumRef.current !== premium;
    const now = Date.now();
    const due = now - lastSyncRef.current >= CAR_SYNC_INTERVAL_MS;

    const sync = () => {
      lastUserRef.current = userId;
      lastPremiumRef.current = premium;
      lastSyncRef.current = Date.now();
      void syncCarSnapshot({ userName, premium });
    };

    if (userChanged || premiumChanged || due) sync();

    // Refresh periodico: senza timer lo snapshot resterebbe fermo all'ultima
    // apertura dell'app (il controllo "due" qui sopra gira solo quando le
    // dipendenze cambiano).
    const timer = window.setInterval(sync, CAR_SYNC_INTERVAL_MS);

    // Rientro in primo piano: l'utente apre l'app → l'auto vede subito i dati
    // aggiornati (non serve aspettare i 5 minuti né ricollegare il veicolo).
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [userId, userName, premium]);
}

export default useCarAutoSync;
